"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useToast } from "@/hooks/use-toast"
import { sendWelcomeEmail } from "@/integrations/resend/client"
import { captureEvent } from "@/utils/analytics";

interface EmailSignupFormProps {
  onSuccess?: () => void
  className?: string
}

export const EmailSignupForm = ({ onSuccess, className = "" }: EmailSignupFormProps) => {
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !name) {
      toast({
        title: "Missing information",
        description: "Please provide both your name and email address.",
        variant: "destructive",
      })
      return
    }

    setIsLoading(true)

    try {
      // Store the email in your database (example with Supabase)
      // const { error } = await supabase.from('newsletter_subscribers').insert({ email, name })

      // Send welcome email
      const result = await sendWelcomeEmail(email, name)

      if (result.success) {
        // Track signup event in PostHog
        captureEvent("signup", { email, name });

        toast({
          title: "Success!",
          description: "Thank you for signing up. Check your inbox for a welcome email.",
        })

        setEmail("")
        setName("")

        if (onSuccess) {
          onSuccess()
        }
      } else {
        throw new Error("Failed to send welcome email")
      }
    } catch (error) {
      console.error("Signup error:", error)
      toast({
        title: "Something went wrong",
        description: "We couldn't process your signup. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      <div>
        <Input
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mb-3"
          disabled={isLoading}
        />
        <Input
          type="email"
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
        />
      </div>
      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? "Processing..." : "Get Updates"}
      </Button>
      <p className="text-xs text-gray-500 text-center">
        By signing up, you agree to our Terms of Service and Privacy Policy.
      </p>
    </form>
  )
}
